/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { strings } from "../../../common/Strings/Strings";
import { SavedReservationProgress } from "../../../models/calendar/SavedReservationProgress";
import { NewReservationPetType } from "../../../models/calendar/NewReservationPetType";
import { saveReservationInProgress } from "../../../util/LocalStorageVariables";
import NewExternalMedicalRecordModal from "../../Pet/History/NewExternalMedicalRecordModal";
import { PetOwnerResponse } from "../../../models/pet/PetOwnerResponse";
import { ClinicPetResponse } from "../../../models/pet/ClinicPetResponse";
import Button from "../../../components/Button";
import { useClinic } from "../../../contexts/ClinicContext";
import { ClinicFeature } from "../../../models/clinic/ClinicFeature";
import Dropdown from "../../../components/Dropdown/Dropdown";

interface Props {
  pet: ClinicPetResponse;
  owner?: PetOwnerResponse;
  reload(): void;
}

const HistoryActions = ({ pet, owner, reload }: Props): ReactElement => {
  const [externalMedicalRecordModal, setExternalMedicalRecordModal] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const { clinic } = useClinic();

  const openExternalMedicalRecordModal = () => {
    setExternalMedicalRecordModal(true);
  };

  const closeExternalMedicalRecordModal = () => {
    setExternalMedicalRecordModal(false);
  };

  const createNewAppointment = () => {
    if (pet && owner) {
      const temp: SavedReservationProgress = {
        petState: {
          state: NewReservationPetType.SELECTED,
          pet: {
            clinicPetDetails: {
              id: pet.clinicPetDetails.id,
              name: pet.clinicPetDetails.name,
              gender: pet.clinicPetDetails.gender,
              species: pet.clinicPetDetails.species,
              breed: pet.clinicPetDetails.breed,
              chronicDiseases: pet.clinicPetDetails.chronicDiseases || [],
              deceased: pet.clinicPetDetails.deceased,
            },
          },
          petOwner: owner,
        },
        symptoms: [],
      };

      saveReservationInProgress(temp);
      navigate("/");
    }
  };

  return (
    <>
      <div className="flex space-x-3 items-center">
        <Button onClick={createNewAppointment}>{strings.newAppointment}</Button>
        <Dropdown className="min-w-48">
          <Dropdown.Toggle />
          <Dropdown.Content>
            {clinic?.availableFeatures?.includes(ClinicFeature.PRESCRIPTION) ? (
              <Link
                className="dropdown-item"
                to={`/prescription/${pet.clinicPetDetails?.id}`}
              >
                {strings.newPrescription}
              </Link>
            ) : (
              <></>
            )}
            <Button
              className="dropdown-item"
              onClick={() => {
                openExternalMedicalRecordModal();
              }}
              variant="link"
            >
              {strings.addNewExternalMedicalRecord}
            </Button>
          </Dropdown.Content>
        </Dropdown>
      </div>
      <NewExternalMedicalRecordModal
        close={closeExternalMedicalRecordModal}
        isOpen={externalMedicalRecordModal}
        petId={pet.clinicPetDetails.id}
        reload={reload}
      />
    </>
  );
};

export default HistoryActions;
